import type { CategoryConfig } from './categoryMeasurementMapping';
import { calculateMaxAllowed } from './categoryMeasurementMapping';

interface MeasurementVars {
  area_sqft: number;
  eave_lf: number;
  rake_lf: number;
  valley_lf: number;
  hip_lf: number;
  ridge_lf: number;
  sidewall_lf: number;
  headwall_lf: number;
  flashing_lf: number;
  chimney_count: number;
  skylight_count: number;
  pipe_1_5_count: number;
  pipe_3_count: number;
  layers: number;
}

interface Product {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
}

interface EstimateLineItem {
  id?: string;
  product_id: string;
  description: string;
  calculation_source?: string;
  category?: string;
}

export interface ProductSelectionRule {
  category: string;
  allowMultiple: boolean;
  maxQuantity?: number | 'measurement';
  measurementField?: keyof MeasurementVars;
  keywords: string[];
  displayName: string;
  helpText: string;
}

export const PRODUCT_SELECTION_RULES: Record<string, ProductSelectionRule> = {
  shingles: {
    category: 'Shingles',
    allowMultiple: false,
    keywords: ['shingle', 'timberline', 'architectural', 'dimensional', 'duration', 'landmark', 'royal sovereign', 'cambridge'],
    displayName: 'Roofing Shingles',
    helpText: 'Choose ONE shingle type for your roof',
  },
  underlayment: {
    category: 'Underlayment',
    allowMultiple: false,
    keywords: ['feltbuster', 'tiger paw', 'deck armor', 'underlayment', 'synthetic', 'felt'],
    displayName: 'Roof Underlayment',
    helpText: 'Choose ONE underlayment product',
  },
  ice_water: {
    category: 'Ice & Water Shield',
    allowMultiple: false,
    keywords: ['ice', 'water', 'shield', 'stormguard', 'weatherwatch', 'storm guard', 'weather watch'],
    displayName: 'Ice & Water Shield',
    helpText: 'Choose ONE ice & water barrier type',
  },
  starter_strip: {
    category: 'Starter Strip',
    allowMultiple: false,
    keywords: ['starter', 'strip', 'weatherblocker', 'weather blocker'],
    displayName: 'Starter Strip',
    helpText: 'Choose ONE starter strip product',
  },
  ridge_cap: {
    category: 'Ridge Cap',
    allowMultiple: false,
    keywords: ['ridge', 'cap', 'seal-a-ridge', 'timbertex', 'ridge cap'],
    displayName: 'Ridge Cap Shingles',
    helpText: 'Choose ONE ridge cap type',
  },
  drip_edge: {
    category: 'Drip Edge',
    allowMultiple: false,
    keywords: ['drip', 'edge', 'aluminum'],
    displayName: 'Drip Edge',
    helpText: 'Choose ONE drip edge style',
  },
  plywood: {
    category: 'Deck Repair',
    allowMultiple: false,
    keywords: ['plywood', 'sheathing', 'decking', 'cdx', 'osb'],
    displayName: 'Roof Decking',
    helpText: 'Choose ONE decking/plywood type',
  },
  tear_off: {
    category: 'Tear-Off',
    allowMultiple: false,
    keywords: ['tear', 'off', 'removal', 'disposal', 'haul', 'debris', 'labor'],
    displayName: 'Tear-Off & Disposal',
    helpText: 'One consolidated tear-off line item',
  },
  pipe_boots: {
    category: 'Buy Boots',
    allowMultiple: true,
    maxQuantity: 'measurement',
    measurementField: 'pipe_1_5_count',
    keywords: ['pipe', 'boot', 'vent pipe', 'buy boot'],
    displayName: 'Buy Boots (Pipe Vents)',
    helpText: 'Select up to the number of pipes entered in measurements',
  },
  skylight: {
    category: 'Skylights',
    allowMultiple: true,
    maxQuantity: 'measurement',
    measurementField: 'skylight_count',
    keywords: ['skylight', 'sun tunnel', 'velux'],
    displayName: 'Skylights',
    helpText: 'Select up to the skylight count in measurements',
  },
  chimney_flashing: {
    category: 'Chimney',
    allowMultiple: true,
    maxQuantity: 'measurement',
    measurementField: 'chimney_count',
    keywords: ['chimney', 'cricket', 'saddle'],
    displayName: 'Chimney Flashings',
    helpText: 'Select up to the chimney count in measurements',
  },
  ventilation: {
    category: 'Ventilation',
    allowMultiple: true,
    keywords: ['vent', 'ridge vent', 'box vent', 'turbine', 'cobra', 'ventilation'],
    displayName: 'Roof Ventilation',
    helpText: 'Add multiple ventilation products as needed',
  },
  shingle_colors: {
    category: 'Shingle Colors',
    allowMultiple: false,
    keywords: ['color', 'uhdz', 'hdz', 'slateline', 'camelot'],
    displayName: 'Shingle Color',
    helpText: 'Choose ONE color for your selected shingle type',
  },
};

// Helper to check if product matches a rule
function matchesProductRule(product: Product, rule: ProductSelectionRule): boolean {
  const searchText = `${product.name} ${product.category} ${product.subcategory || ''}`.toLowerCase();
  return rule.keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
}

// Get selection rule for a product
export function getSelectionRuleForProduct(product: Product): ProductSelectionRule | null {
  for (const rule of Object.values(PRODUCT_SELECTION_RULES)) {
    if (matchesProductRule(product, rule)) {
      return rule;
    }
  }
  return null;
}

// Find conflicting line items (for mutual exclusivity)
export function findConflictingLineItem(
  product: Product,
  existingLineItems: EstimateLineItem[]
): EstimateLineItem | null {
  const rule = getSelectionRuleForProduct(product);
  if (!rule || rule.allowMultiple) return null;

  // Find existing item that matches the same rule (but is a different product)
  for (const item of existingLineItems) {
    const itemMatchesRule = rule.keywords.some(kw => 
      item.description.toLowerCase().includes(kw.toLowerCase())
    );
    if (itemMatchesRule && item.product_id !== product.id) {
      return item;
    }
  }

  return null;
}

// Check if product will replace another (for default product swapping)
export function canAddMoreOfProduct(
  product: Product,
  existingLineItems: EstimateLineItem[],
  measurementVars: MeasurementVars,
  categoryConfig?: CategoryConfig | null
): { 
  canAdd: boolean; 
  currentCount: number; 
  maxAllowed: number;
} {
  // Always allow selection - no automatic replacement
  return { 
    canAdd: true, 
    currentCount: 0, 
    maxAllowed: Infinity 
  };
}
